declare global {
  interface Window {
    BrowserPrint?: {
      getDefaultDevice: (
        type: string,
        success: (device: ZebraDevice | null) => void,
        error: (error: string) => void
      ) => void;
      getLocalDevices: (
        success: (devices: ZebraDevice[]) => void,
        error: (error: string) => void,
        type: string
      ) => void;
      getApplicationConfiguration?: (
        success: (config: unknown) => void,
        error: (error: string) => void
      ) => void;
    };
  }
}

export interface ZebraDevice {
  name: string;
  deviceType: string;
  uid: string;
  connection: string;
  send: (
    data: string,
    success: () => void,
    error: (error: string) => void
  ) => void;
}

/**
 * Como o sistema atual: o JS fica hospedado no próprio admin.
 * O BrowserPrint-3.0.216 fala com o agente local (9100/9101) só no write/available.
 */
const HOSTED_SCRIPT = '/browser-print/BrowserPrint-3.0.216.min.js';

const SERVICE_UNAVAILABLE_MSG =
  'Zebra Browser Print indisponível. Abra o app neste terminal, defina a ZD220 como padrão e autorize hml-nexus-admin.consolare.com.br.';

let loadPromise: Promise<void> | null = null;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const removeZebraScripts = () => {
  document
    .querySelectorAll('script[data-zebra-bp]')
    .forEach((el) => el.parentElement?.removeChild(el));
};

const waitForBrowserPrint = async (timeoutMs = 3000): Promise<boolean> => {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (window.BrowserPrint?.getDefaultDevice) return true;
    await sleep(100);
  }
  return Boolean(window.BrowserPrint?.getDefaultDevice);
};

const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const previous = document.querySelector(`script[data-zebra-bp="${src}"]`);
    if (previous) {
      previous.parentElement?.removeChild(previous);
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.zebraBp = src;
    script.onload = () => resolve();
    script.onerror = () => {
      script.parentElement?.removeChild(script);
      reject(new Error(`Falha ao carregar ${src}`));
    };
    document.head.appendChild(script);
  });

export const ensureBrowserPrintLoaded = async (): Promise<void> => {
  if (window.BrowserPrint?.getDefaultDevice) return;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    if (!window.BrowserPrint?.getDefaultDevice) {
      removeZebraScripts();
    }

    await loadScript(HOSTED_SCRIPT);
    const ready = await waitForBrowserPrint();
    if (!ready) {
      removeZebraScripts();
      throw new Error(SERVICE_UNAVAILABLE_MSG);
    }
  })();

  try {
    await loadPromise;
  } catch (error) {
    loadPromise = null;
    throw error instanceof Error ? error : new Error(SERVICE_UNAVAILABLE_MSG);
  }
};

export const getDefaultZebraPrinter = async (): Promise<ZebraDevice> => {
  await ensureBrowserPrintLoaded();

  return new Promise((resolve, reject) => {
    if (!window.BrowserPrint?.getDefaultDevice) {
      reject(new Error(SERVICE_UNAVAILABLE_MSG));
      return;
    }

    window.BrowserPrint.getDefaultDevice(
      'printer',
      (device) => {
        if (device) {
          resolve(device);
          return;
        }

        window.BrowserPrint!.getLocalDevices(
          (devices) => {
            const printer =
              devices?.find((d) => (d.deviceType || '').toLowerCase() === 'printer') ||
              devices?.[0];
            if (printer) resolve(printer);
            else {
              reject(
                new Error(
                  'Nenhuma impressora no Browser Print. Defina a ZD220 como padrão e autorize este site.'
                )
              );
            }
          },
          (error) =>
            reject(new Error(error || 'Erro ao listar impressoras no Browser Print')),
          'printer'
        );
      },
      (error) =>
        reject(
          new Error(
            error ||
              'Não foi possível obter a impressora padrão. Verifique se o Zebra Browser Print está aberto.'
          )
        )
    );
  });
};

export const sendZplToDefaultPrinter = async (zpl: string): Promise<void> => {
  const device = await getDefaultZebraPrinter();

  return new Promise((resolve, reject) => {
    device.send(
      zpl,
      () => resolve(),
      (error) =>
        reject(
          new Error(
            error ||
              'Falha ao enviar para a Zebra. Confira se está ligada e é a padrão no Browser Print.'
          )
        )
    );
  });
};
